<template>
    <TemplateModalEx v-model:show="computedShow" class="passwordManagerDialog">
        <template #title>パスワードマネージャー設定</template>

        <template #body>
            <span style="margin-bottom: 16px; height:40px;">
                端末利用者に利用を許可するパスワードマネージャー（パスワード／パスキーを管理するアプリ）を登録してください。<br>
                ※事前に対象のパスワードマネージャーを、{{ infoText }}に登録する必要があります。
            </span>
            <ListTitleBar ref="refTitleBar" titleCaption="利用を許可するパスワードマネージャー" btnCaption_1="追加" btnCaption_2="削除"
                :btnActiveType_1="0" :btnActiveType_2="3" @onBtnClicked_1="onAdd"
                @onBtnClicked_2="onDelete" :selectCount="selectCount" :rowCount="rowCount" :rowCountArea="true"
                :enableSearchText="true" @onSearchBoxKeydown="search" @onBtnClear="search" />
            <ListCtrl ref="refPasswordManagedListCtrl" :columns="PASSWORD_MANAGER_COLUMN" :selectType="2"
                :getCellTextFunc="getCellText" :getCellHTMLFunc="getCellHTML" @onClickCell="onClickCell"
                style="height: 300px" />
        </template>

        <template #footer>
            <div class="d-flex flex-row justify-content-center align-items-center">
                <div class="passwordManagerDialogFooter">
                    <button class="btn-scv-blue margin-r" @click="onApply" :disabled="!isDirty">OK</button>
                    <button class="btn-scv-white" @click="onClose">キャンセル</button>
                </div>
            </div>
        </template>
    </TemplateModalEx>
    <Teleport to="body">
        <!-- Google Playストアのアプリケーションの詳細情報 -->
        <AppMgrApplicationDetailModal ref="refDetailModal" v-model:show="showDetailDialog"/>
        <PasswordManagerAddDialog v-model:show="showPasswordManagedDialog"
            :androidAppMgrApplicationSetting="unCredentialProvider" @modified="onUpdateModelValue" />
    </Teleport>
</template>
<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { cloneDeep } from 'lodash';
import ListTitleBar from '@/components/ListTitleBar.vue';
import { ListCtrl } from 'lib-vue-v2/components';
import { SearchColumnData, SearchColumnType } from '@/models/SearchColumnData';
import { TemplateModalEx } from "lib-vue-v2/components";
import { AndroidAppMgrApplicationSetting } from '@/models/Mobile/AndroidAppDist/AndroidAppMgrApplicationSetting'
import { AndroidAppDistDefinition } from "@/components/Mobile/AndroidAppDist/AndroidAppDistDefinition";
import { ApplicationPolicyConverter } from '@/models/Mobile/AndroidAppDist/ApplicationPolicyConverter'
import AppMgrApplicationDetailModal from '@/components/Mobile/AndroidAppDist/AppMgrApplicationDetailModal.vue';
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants';
import PasswordManagerAddDialog from '@/components/Mobile/AndroidAppDist/PasswordManagerAddDialog.vue';


// --- Define ---
// リスト定義
const PASSWORD_MANAGER_COLUMN: Array<SearchColumnData> = [
    { columnID: 0, dataType: SearchColumnType.icon | SearchColumnType.no_sort, text: "" },
    { columnID: 1, dataType: SearchColumnType.string | SearchColumnType.link, text: "アプリケーション名" },
    { columnID: 2, dataType: SearchColumnType.string, text: "パッケージ名" },
    { columnID: 3, dataType: SearchColumnType.string, text: "ステータス" },
];

interface Props {
    // ダイアログを表示するかどうか
    show: boolean;
    mode: number;   // 0:White/1:Black
    androidAppMgrApplicationSetting: AndroidAppMgrApplicationSetting[];
}
const props = withDefaults(defineProps<Props>(), {});

interface Emits {
    (event: "update:show", newValue: boolean): void;
    (event: "modified", newValue: AndroidAppMgrApplicationSetting[]): void;
}
const emit = defineEmits<Emits>();

// 表示非表示の切り替え
const refShow = ref(false);
const computedShow = computed({
    get: () => refShow.value,
    set: (newValue: boolean) => emit("update:show", newValue)
});

// --- ref ---
const refTitleBar = ref<InstanceType<typeof ListTitleBar> | null>(null);
const refPasswordManagedListCtrl = ref<InstanceType<typeof ListCtrl> | null>(null);
const refDetailModal = ref<InstanceType<typeof AppMgrApplicationDetailModal> | null>(null);
const isDirty = ref(false);

// 編集中データ
const editSetting = ref<AndroidAppMgrApplicationSetting[]>([]);

// ポリシー⇔UIデータ
const converter = new ApplicationPolicyConverter();

// 編集ダイアログの表示／非表示
const showPasswordManagedDialog = ref(false);

// アプリ詳細ダイアログの表示／非表示
const showDetailDialog = ref(false);

// --- Watch ---
watch(() => props.show, async () => {
    if (props.show) {
        initData();
        refShow.value = true;
    } else {
        refShow.value = false;
    }
}, { immediate: true });

// --- Computed ---
const infoText = computed(() => {
    if (props.mode === 0) return "「利用を許可するアプリケーション（ホワイトリスト）」";
    return "「利用設定するアプリケーション」";
});

const selectCount = computed(() => {
    if (refPasswordManagedListCtrl.value === null) return 0;
    return refPasswordManagedListCtrl.value.getSelectListIndex().length;
});

const rowCount = computed(() => {
    if (refPasswordManagedListCtrl.value === null) return 0;
    return refPasswordManagedListCtrl.value.getDisplayListIndex().length;
});

const credentialProvider = computed(() => {
    return editSetting.value.filter(e => e.policyJson.credentialProviderPolicy === schemaConstants.AMAPI_CredentialProviderPolicy_Enums.credentialProviderAllowed);
});

const unCredentialProvider = computed(() => {
    return editSetting.value.filter(e => e.policyJson.credentialProviderPolicy !== schemaConstants.AMAPI_CredentialProviderPolicy_Enums.credentialProviderAllowed);
});

// 適用
function onApply(): void {
    emit("modified", editSetting.value);
    emit("update:show", false);
}

// 閉じる
function onClose(): void {
    emit("update:show", false);
}

// 追加
const onAdd = () => {
    showPasswordManagedDialog.value = true;
}

// 削除
const onDelete = () => {
    if (!refPasswordManagedListCtrl.value) return;
    const selectedIndices: Array<number> = refPasswordManagedListCtrl.value?.getSelectListIndex();
    const selectedPackageNames = new Set(
        selectedIndices.map(index => credentialProvider.value[index].basicInfo.packageName)
    );
    editSetting.value.forEach((item) => {
        if (selectedPackageNames.has(item.basicInfo.packageName)) {
            item.policyJson.credentialProviderPolicy =
                schemaConstants.AMAPI_CredentialProviderPolicy_Enums.credentialProviderPolicyUnspecified;
        }
    });
    isDirty.value = true;
    updateList();
}

// 初期化
const initData = () => {
    isDirty.value = false;
    refTitleBar.value?.SearchStart("");
    editSetting.value = cloneDeep(props.androidAppMgrApplicationSetting);
    updateList();
}

// 検索
const search = (searchText: string = ""): void => {
    refPasswordManagedListCtrl.value?.search(searchText);
}

// リスト更新
const updateList = () => {
    refPasswordManagedListCtrl.value?.updateList(credentialProvider.value.length);
}

// リスト作成処理(テキスト)
const getCellText = (columnId: number, index: number): string => {
    switch (columnId) {
        // アプリケーション名
        case 1:
            // パッケージ名手動入力アプリは、通常テキストでアプリケーション名を表示する
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === credentialProvider.value[index].basicInfo.appSettingType) return credentialProvider.value[index].basicInfo.appName;
            return "";
        // パッケージ名
        case 2:
            return converter.GetPackageName(credentialProvider.value[index].policyJson);
        // ステータス
        case 3:
            return converter.GetDisabledText(credentialProvider.value[index].policyJson);
    }
    return "";
}

// リスト作成処理(HTML)
const getCellHTML = (columnId: number, index: number): string => {
    switch (columnId) {
        // アイコン
        case 0:
            // パッケージ名手動入力アプリはアイコンなし
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === credentialProvider.value[index].basicInfo.appSettingType) {
                return "<div style=\"padding: 8px 0px;\"><div style=\"height: 32px; width: 32px;\"></div></div>";
            }
            if (0 !== credentialProvider.value[index].basicInfo.iconUrl.length) {
                return "<div style=\"padding: 8px 0px;\"><img src=\"" + credentialProvider.value[index].basicInfo.iconUrl + "\" style=\"height: 32px; width: 32px; border-radius: 7px; border: 0.5px solid #E6EBF0\"></div>";
            }
            else {
                return "<div style=\"padding: 8px 0px;\"><div style=\"height: 32px; width: 32px; border-radius: 7px; border: 0.5px solid #E6EBF0\"></div></div>";
            }

        // アプリケーション名
        case 1:
            // パッケージ名手動入力アプリは通常テキスト
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === credentialProvider.value[index].basicInfo.appSettingType) {
                return "";
            }
            // リンク付き
            return "<span class=\"scv-link-text\" style=\"cursor: pointer;\">" + credentialProvider.value[index].basicInfo.appName + "</span>";
    }
    return "";
}

// リスト作成処理(HTML)
const onClickCell = (columnId: number, index: number): void => {
    switch (columnId) {
        // アプリケーション名
        case 1:
            // パッケージ名手動入力アプリは通常テキスト
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === credentialProvider.value[index].basicInfo.appSettingType) return;
            showDetail(credentialProvider.value[index]);
            return;
    }
}

// 詳細表示
const showDetail = (data: AndroidAppMgrApplicationSetting): void => {
    if (!refDetailModal.value) return;
    refDetailModal.value.SetData(data.basicInfo);
    showDetailDialog.value = true;
}

// パスワードマネージャ追加
const onUpdateModelValue = (packageNames: string[]): void => {
    const nameSet = new Set(packageNames);
    editSetting.value.forEach((item, index) => {
        if (nameSet.has(item.basicInfo.packageName)) {
            if (editSetting.value[index]) {
                editSetting.value[index].policyJson.credentialProviderPolicy =
                    schemaConstants.AMAPI_CredentialProviderPolicy_Enums.credentialProviderAllowed;
            }
        }
    });
    isDirty.value = true;
    updateList();
}

</script>

<style lang="scss" scoped>
.passwordManagerDialog {
    :deep(.modal-content) {
        width: 960px;
    }
}

.margin-r {
    margin-right: 24px;
}

.passwordManagerDialogFooter {
    display: flex;
    flex-direction: row;
    align-items: center;

    .margin-r {
        margin-right: 24px;
    }
}
</style>
